import { useState, useEffect, useMemo, useCallback } from "react";
import type { CSSProperties, FormEvent } from "react";

export interface Timeslot {
  start_time: string;
  end_time: string;
}

export interface AppointmentPayload {
  customer_name: string;
  customer_phone: string;
  start_time: string;
  end_time: string;
  notes?: string;
}

export interface BookingTexts {
  dateStepTitle: string;
  timeStepTitle: string;
  reviewStepTitle: string;
  dateHint: string;
  timeHint: string;
  reviewHint: string;
  next: string;
  prev: string;
  modifyTime: string;
  submit: string;
  submitting: string;
  success: string;
  loading: string;
  noSlots: string;
  error: string;
  nameLabel: string;
  namePlaceholder: string;
  phoneLabel: string;
  phonePlaceholder: string;
  notesLabel: string;
  notesPlaceholder: string;
  nameRequired: string;
  phoneRequired: string;
  reviewDateLabel: string;
  reviewTimeLabel: string;
  reviewNotesLabel: string;
  weekdays: string[];
  monthNames: string[];
}

export interface BookingCalendarProps {
  timeslotsApi: () => Promise<Timeslot[]>;
  appointmentsApi: (payload: AppointmentPayload) => Promise<unknown>;
  texts: BookingTexts;
  containerStyle?: CSSProperties;
}

function toDateKey(value: string): string {
  return value.split(" ")[0];
}

function toTime(value: string): string {
  const time = value.split(" ")[1] || "";
  return time.slice(0, 5);
}

function todayKey(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function pad(value: number): string {
  return String(value).padStart(2, "0");
}

export default function BookingCalendar({
  timeslotsApi,
  appointmentsApi,
  texts,
  containerStyle,
}: BookingCalendarProps) {
  const [slots, setSlots] = useState<Timeslot[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const [step, setStep] = useState<0 | 1 | 2>(0);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<Timeslot | null>(null);
  const [currentMonth, setCurrentMonth] = useState<string>("");

  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [formError, setFormError] = useState("");

  const fetchSlots = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const data = await timeslotsApi();
      setSlots(Array.isArray(data) ? data : []);
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [timeslotsApi]);

  useEffect(() => {
    fetchSlots();
  }, [fetchSlots]);

  const availableDates = useMemo(() => {
    const set = new Set<string>();
    for (const slot of slots) {
      set.add(toDateKey(slot.start_time));
    }
    return Array.from(set).sort();
  }, [slots]);

  const availableMonths = useMemo(
    () => new Set(availableDates.map((d) => d.slice(0, 7))),
    [availableDates]
  );

  useEffect(() => {
    if (availableDates.length === 0) return;
    const today = todayKey();
    const closest =
      availableDates.find((d) => d >= today) ??
      availableDates[availableDates.length - 1];

    setSelectedDate((prev) =>
      prev && availableDates.includes(prev) ? prev : closest
    );
    setCurrentMonth((prev) =>
      availableMonths.has(prev) ? prev : closest.slice(0, 7)
    );
  }, [availableDates, availableMonths]);

  const year = currentMonth ? Number(currentMonth.split("-")[0]) : 0;
  const month = currentMonth ? Number(currentMonth.split("-")[1]) : 0;

  const daySlots = useMemo(() => {
    if (!selectedDate) return [];
    return slots
      .filter((slot) => toDateKey(slot.start_time) === selectedDate)
      .sort((a, b) => a.start_time.localeCompare(b.start_time));
  }, [slots, selectedDate]);

  const calendar = useMemo(() => {
    if (!year || !month) return { cells: [] as (number | null)[] };
    const firstWeekday = new Date(year, month - 1, 1).getDay();
    const daysInMonth = new Date(year, month, 0).getDate();
    const cells: (number | null)[] = [];
    for (let i = 0; i < firstWeekday; i += 1) cells.push(null);
    for (let d = 1; d <= daysInMonth; d += 1) cells.push(d);
    return { cells };
  }, [year, month]);

  const goPrevMonth = () => {
    if (!currentMonth) return;
    const prevDate = new Date(year, month - 2, 1);
    setCurrentMonth(
      `${prevDate.getFullYear()}-${pad(prevDate.getMonth() + 1)}`
    );
  };

  const goNextMonth = () => {
    if (!currentMonth) return;
    const nextDate = new Date(year, month, 1);
    setCurrentMonth(
      `${nextDate.getFullYear()}-${pad(nextDate.getMonth() + 1)}`
    );
  };

  const handleSelectDate = (dateKey: string) => {
    setSelectedDate(dateKey);
    setStep(1);
  };

  const handleSelectSlot = (slot: Timeslot) => {
    setSelectedSlot(slot);
    setStep(2);
  };

  const handleModifyTime = () => {
    setStep(1);
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError("");

    if (!name.trim()) {
      setFormError(texts.nameRequired);
      return;
    }
    if (!phone.trim()) {
      setFormError(texts.phoneRequired);
      return;
    }
    if (!selectedSlot) return;

    setSubmitting(true);
    setSuccess(false);
    try {
      await appointmentsApi({
        customer_name: name.trim(),
        customer_phone: phone.trim(),
        start_time: selectedSlot.start_time,
        end_time: selectedSlot.end_time,
        notes: notes.trim(),
      });

      setSuccess(true);
      setName("");
      setPhone("");
      setNotes("");
      setSelectedSlot(null);
      setSelectedDate(null);
      setStep(0);
      await fetchSlots();
    } catch {
      setFormError(texts.error);
    } finally {
      setSubmitting(false);
    }
  };

  const steps = [texts.dateStepTitle, texts.timeStepTitle, texts.reviewStepTitle];

  return (
    <div
      style={containerStyle}
      className="rounded-2xl bg-white border border-background-200/70 p-6 md:p-8"
    >
      {success && (
        <div className="mb-6 flex items-center gap-3 rounded-lg bg-secondary-50 border border-secondary-200 px-4 py-3">
          <i className="ri-checkbox-circle-line text-xl text-secondary-600"></i>
          <p className="text-sm font-medium text-secondary-900">{texts.success}</p>
        </div>
      )}

      {/* 단계 표시 */}
      <div className="flex items-center gap-2 mb-8">
        {steps.map((label, idx) => {
          const isActive = step === idx;
          const isDone = step > idx;
          return (
            <div key={label} className="flex items-center gap-2">
              <div className="flex items-center gap-2">
                <span
                  className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold whitespace-nowrap ${
                    isDone
                      ? "bg-secondary-500 text-white"
                      : isActive
                      ? "bg-primary-500 text-white"
                      : "bg-background-100 text-foreground-500"
                  }`}
                >
                  {isDone ? (
                    <i className="ri-check-line"></i>
                  ) : (
                    idx + 1
                  )}
                </span>
                <span
                  className={`text-sm whitespace-nowrap ${
                    isActive
                      ? "font-semibold text-foreground-900"
                      : "text-foreground-500"
                  }`}
                >
                  {label}
                </span>
              </div>
              {idx < steps.length - 1 && (
                <span className="w-6 h-px bg-background-300"></span>
              )}
            </div>
          );
        })}
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center gap-3 py-16">
          <i className="ri-loader-4-line animate-spin text-3xl text-primary-400"></i>
          <p className="text-sm text-foreground-500">{texts.loading}</p>
        </div>
      ) : loadError ? (
        <div className="flex flex-col items-center justify-center gap-4 py-16 text-center">
          <i className="ri-error-warning-line text-3xl text-primary-400"></i>
          <p className="text-sm text-foreground-600">{texts.error}</p>
          <button
            type="button"
            onClick={fetchSlots}
            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary-500 text-white text-sm font-semibold whitespace-nowrap transition-colors hover:bg-primary-600 cursor-pointer"
          >
            <i className="ri-refresh-line"></i>
            {texts.prev}
          </button>
        </div>
      ) : slots.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
          <i className="ri-calendar-line text-3xl text-foreground-300"></i>
          <p className="text-sm text-foreground-500">{texts.noSlots}</p>
        </div>
      ) : step === 0 ? (
        /* 1단계: 날짜 선택 */
        <div className="flex flex-col gap-6">
          <div className="flex items-center justify-between">
            <h4 className="text-base font-semibold text-foreground-900">
              {texts.monthNames[month - 1]} {year}
            </h4>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={goPrevMonth}
                aria-label={texts.prev}
                className="w-9 h-9 rounded-full bg-background-100 flex items-center justify-center text-foreground-600 transition-colors hover:bg-primary-50 hover:text-primary-600 cursor-pointer"
              >
                <i className="ri-arrow-left-s-line text-xl"></i>
              </button>
              <button
                type="button"
                onClick={goNextMonth}
                aria-label={texts.next}
                className="w-9 h-9 rounded-full bg-background-100 flex items-center justify-center text-foreground-600 transition-colors hover:bg-primary-50 hover:text-primary-600 cursor-pointer"
              >
                <i className="ri-arrow-right-s-line text-xl"></i>
              </button>
            </div>
          </div>

          <div className="grid grid-cols-7 gap-1.5">
            {calendar.cells.map((day, idx) => {
              if (day === null) {
                return <div key={`empty-${idx}`} />;
              }
              const dateKey = `${year}-${pad(month)}-${pad(day)}`;
              const weekday = new Date(year, month - 1, day).getDay();
              const available = availableDates.includes(dateKey);
              const isSelected = selectedDate === dateKey;

              return (
                <button
                  key={dateKey}
                  type="button"
                  disabled={!available}
                  onClick={() => handleSelectDate(dateKey)}
                  className={`rounded-lg py-2 flex flex-col items-center justify-center gap-0.5 transition-colors ${
                    isSelected
                      ? "bg-primary-500 text-white"
                      : available
                      ? "bg-primary-50 text-primary-700 hover:bg-primary-100 cursor-pointer"
                      : "text-foreground-300 cursor-not-allowed"
                  }`}
                >
                  <span
                    className={`text-[10px] leading-none ${
                      isSelected ? "text-white/80" : "text-foreground-400"
                    }`}
                  >
                    {texts.weekdays[weekday]}
                  </span>
                  <span className="text-sm font-semibold leading-none">
                    {day}
                  </span>
                </button>
              );
            })}
          </div>

          <p className="text-xs text-foreground-500 flex items-center gap-1.5">
            <i className="ri-information-line"></i>
            {texts.dateHint}
          </p>
        </div>
      ) : step === 1 ? (
        /* 2단계: 시간 선택 */
        <div className="flex flex-col gap-6">
          <div className="flex items-center justify-between">
            <div className="flex flex-col gap-1">
              <h4 className="text-base font-semibold text-foreground-900">
                {texts.timeStepTitle}
              </h4>
              <span className="text-sm text-foreground-500">
                {year}.{pad(month)}.{selectedDate ? selectedDate.slice(8, 10) : ""}
              </span>
            </div>
            <button
              type="button"
              onClick={() => setStep(0)}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-foreground-600 transition-colors hover:text-primary-600 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              {texts.prev}
            </button>
          </div>

          {daySlots.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-3 py-10 text-center">
              <i className="ri-time-line text-2xl text-foreground-300"></i>
              <p className="text-sm text-foreground-500">{texts.noSlots}</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {daySlots.map((slot) => (
                <button
                  key={slot.start_time}
                  type="button"
                  onClick={() => handleSelectSlot(slot)}
                  className="flex flex-col items-center gap-1 rounded-lg border border-background-200 bg-background-50 px-4 py-4 transition-colors hover:border-primary-300 hover:bg-primary-50 cursor-pointer whitespace-nowrap"
                >
                  <span className="text-base font-semibold text-foreground-900">
                    {toTime(slot.start_time)}
                  </span>
                  <span className="text-xs text-foreground-500">
                    {toTime(slot.start_time)} - {toTime(slot.end_time)}
                  </span>
                </button>
              ))}
            </div>
          )}

          <p className="text-xs text-foreground-500 flex items-center gap-1.5">
            <i className="ri-information-line"></i>
            {texts.timeHint}
          </p>
        </div>
      ) : (
        /* 3단계: 정보 확인 및 입력 */
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          <div className="lg:col-span-2 flex flex-col gap-4">
            <div className="rounded-xl bg-background-50 border border-background-200/70 p-5">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-sm font-semibold text-foreground-900">
                  {texts.reviewStepTitle}
                </h4>
                <button
                  type="button"
                  onClick={handleModifyTime}
                  className="inline-flex items-center gap-1 text-sm font-medium text-primary-600 transition-colors hover:text-primary-700 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-edit-2-line"></i>
                  {texts.modifyTime}
                </button>
              </div>

              <div className="flex flex-col gap-3">
                <div className="flex items-start gap-2">
                  <i className="ri-calendar-line text-primary-500 mt-0.5"></i>
                  <div className="flex flex-col gap-0.5">
                    <span className="text-xs text-foreground-500">
                      {texts.reviewDateLabel}
                    </span>
                    <span className="text-sm font-semibold text-foreground-900">
                      {selectedSlot ? toDateKey(selectedSlot.start_time) : ""}
                    </span>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <i className="ri-time-line text-primary-500 mt-0.5"></i>
                  <div className="flex flex-col gap-0.5">
                    <span className="text-xs text-foreground-500">
                      {texts.reviewTimeLabel}
                    </span>
                    <span className="text-sm font-semibold text-foreground-900">
                      {selectedSlot
                        ? `${toTime(selectedSlot.start_time)} - ${toTime(
                            selectedSlot.end_time
                          )}`
                        : ""}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <p className="text-xs text-foreground-500 leading-relaxed">
              {texts.reviewHint}
            </p>
          </div>

          <form
            onSubmit={handleSubmit}
            className="lg:col-span-3 flex flex-col gap-4"
          >
            <div className="flex flex-col gap-1.5">
              <label
                htmlFor="booking-name"
                className="text-sm font-medium text-foreground-700"
              >
                {texts.nameLabel}
              </label>
              <input
                id="booking-name"
                name="customer_name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={texts.namePlaceholder}
                className="w-full px-4 py-3 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100"
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label
                htmlFor="booking-phone"
                className="text-sm font-medium text-foreground-700"
              >
                {texts.phoneLabel}
              </label>
              <input
                id="booking-phone"
                name="customer_phone"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder={texts.phonePlaceholder}
                className="w-full px-4 py-3 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100"
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label
                htmlFor="booking-notes"
                className="text-sm font-medium text-foreground-700"
              >
                {texts.notesLabel}
              </label>
              <textarea
                id="booking-notes"
                name="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                maxLength={500}
                rows={4}
                placeholder={texts.notesPlaceholder}
                className="w-full px-4 py-3 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 resize-none"
              />
            </div>

            {formError && (
              <p className="text-sm font-medium text-primary-600 flex items-center gap-1.5">
                <i className="ri-error-warning-line"></i>
                {formError}
              </p>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-primary-500 text-white text-base font-semibold whitespace-nowrap transition-colors hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer"
            >
              {submitting ? (
                <>
                  <i className="ri-loader-4-line animate-spin"></i>
                  {texts.submitting}
                </>
              ) : (
                <>
                  <i className="ri-check-line"></i>
                  {texts.submit}
                </>
              )}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}